import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { TotalReachLogo } from "../components/Logo";
import { Card, Btn, Input, Select } from "../components/UI";
import { PartyBadge } from "./CandidateDirectory";
import { Loader2, Crown, LogIn, Vote } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","VI","GU"];
const REGIONS = ["Northeast", "Midwest", "South", "West", "Territories"];

export default function PublicDirectory() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ region: "", state: "", office: "", search: "" });

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setLoading(true);
    Promise.all([
      api.get("/public/midterms/stats"),
      api.get("/public/midterms/candidates", { params }),
    ]).then(([s, c]) => { setStats(s.data); setCandidates(c.data); }).finally(() => setLoading(false));
  }, [JSON.stringify(f)]); // eslint-disable-line

  const races = {};
  candidates.forEach(c => { (races[c.race_id] = races[c.race_id] || []).push(c); });

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white">
      <header className="h-16 border-b border-white/10 flex items-center px-6 gap-4 sticky top-0 bg-[#0A0E1A]/80 backdrop-blur-xl z-30">
        <TotalReachLogo />
        <span className="text-xs text-slate-500 hidden md:block">2026 Midterm Candidate Directory</span>
        <div className="ml-auto">
          <Btn onClick={() => navigate("/login")} data-testid="public-signin-btn"><LogIn className="w-4 h-4" /> Sign in to claim your page</Btn>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8">
        <div className="pt-6">
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-3">Live FEC data · 2026 Midterms</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">
            Every declared midterm candidate. Every region. One directory.
          </h1>
          <p className="mt-3 text-base text-slate-400 max-w-2xl">
            Browse {stats ? stats.total_candidates.toLocaleString() : "…"} Senate and House candidates across {stats ? stats.total_races : "…"} races.
            Candidates: claim your page to unlock competitor tracking, poll trends, and AI-powered campaign strategy.
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {REGIONS.map(r => (
              <button key={r} onClick={() => setF({ ...f, region: f.region === r ? "" : r, state: "" })} data-testid={`public-region-${r}`}
                className={`glass-card rounded-lg p-4 text-left transition-colors ${f.region === r ? "border-cyan-400/50" : "hover:border-white/20"}`}>
                <div className="font-display text-2xl font-bold">{stats.by_region?.[r] || 0}</div>
                <div className="text-xs text-slate-400 mt-1">{r}</div>
              </button>
            ))}
          </div>
        )}

        <Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Select label="State" value={f.state} onChange={e => setF({ ...f, state: e.target.value })} data-testid="public-state-filter">
              <option value="">All states</option>
              {US_STATES.map(s => <option key={s}>{s}</option>)}
            </Select>
            <Select label="Office" value={f.office} onChange={e => setF({ ...f, office: e.target.value })} data-testid="public-office-filter">
              <option value="">Senate + House</option>
              <option value="S">Senate</option>
              <option value="H">House</option>
            </Select>
            <Input label="Search name" value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder="Candidate name…" className="col-span-2" data-testid="public-search" />
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading…</div>
          ) : (
            <div className="space-y-3" data-testid="public-races-list">
              {Object.entries(races).slice(0, 60).map(([raceId, list]) => {
                const first = list[0];
                return (
                  <div key={raceId} className="border border-white/10 rounded-lg px-4 py-3 hover:border-cyan-400/30 transition-colors cursor-pointer flex flex-wrap items-center gap-3"
                    onClick={() => navigate(`/directory/race/${raceId}`)} data-testid={`public-race-${raceId}`}>
                    <Vote className="w-4 h-4 text-cyan-300 shrink-0" />
                    <div className="font-display font-bold text-sm min-w-[140px]">
                      {first.office_full} · {first.state}{first.office === "H" && `-${first.district}`}
                    </div>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {list.slice(0, 4).map(c => (
                        <span key={c.id} className="inline-flex items-center gap-1.5 text-xs text-slate-300 bg-white/[0.03] rounded-full px-2 py-0.5">
                          {c.name.split(",")[0]} <PartyBadge party={c.party} />
                          {c.claim_status === "approved" && <Crown className="w-3 h-3 text-cyan-300" />}
                        </span>
                      ))}
                      {list.length > 4 && <span className="text-xs text-slate-500">+{list.length - 4}</span>}
                    </div>
                    <span className="text-xs text-cyan-300 shrink-0">Polls & trends →</span>
                  </div>
                );
              })}
              {Object.keys(races).length === 0 && <div className="py-10 text-center text-sm text-slate-500">No candidates match.</div>}
            </div>
          )}
        </Card>

        <div className="text-center text-xs text-slate-600 pb-10">
          Candidate roster: live FEC.gov data · Poll trends shown are demo tracking data · Powered by TotalReach.us
        </div>
      </main>
    </div>
  );
}
